import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    Shield,
    Users,
    Search,
    MoreVertical,
    ShieldCheck,
    ShieldOff,
    Loader2,
    UserX,
    UserCheck
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Navigate } from 'react-router-dom';

export default function Admin() {
    const { api, isAdmin, user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [actionType, setActionType] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        if (isAdmin) {
            fetchUsers();
        }
    }, [isAdmin, fetchUsers]);

    if (!isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())
    );

    const handleAction = async () => {
        if (!selectedUser || !actionType) return;

        try {
            switch (actionType) {
                case 'promote':
                    await api.put(`/admin/users/${selectedUser.id}/role?role=admin`);
                    toast.success(`${selectedUser.name} is now an admin`);
                    break;
                case 'demote':
                    await api.put(`/admin/users/${selectedUser.id}/role?role=member`);
                    toast.success(`${selectedUser.name} is now a member`);
                    break;
                case 'deactivate':
                    await api.put(`/admin/users/${selectedUser.id}/deactivate`);
                    toast.success(`${selectedUser.name} has been deactivated`);
                    break;
                case 'activate':
                    await api.put(`/admin/users/${selectedUser.id}/activate`);
                    toast.success(`${selectedUser.name} has been activated`);
                    break;
            }
            fetchUsers();
        } catch (error) {
            console.error('Action failed:', error);
            toast.error(error.response?.data?.detail || 'Action failed');
        } finally {
            setActionDialogOpen(false);
            setSelectedUser(null);
            setActionType(null);
        }
    };

    const openActionDialog = (userItem, action) => {
        setSelectedUser(userItem);
        setActionType(action);
        setActionDialogOpen(true);
    };

    const getInitials = (name) => {
        return name
            ?.split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U';
    };

    const getActionMessage = () => {
        switch (actionType) {
            case 'promote':
                return `Are you sure you want to make ${selectedUser?.name} an admin? They will have full access to all features.`;
            case 'demote':
                return `Are you sure you want to remove admin privileges from ${selectedUser?.name}?`;
            case 'deactivate':
                return `Are you sure you want to deactivate ${selectedUser?.name}? They will no longer be able to log in.`;
            case 'activate':
                return `Are you sure you want to reactivate ${selectedUser?.name}? They will be able to log in again.`;
            default:
                return '';
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-2">
                        <Shield className="h-8 w-8 text-fuchsia-500" />
                        Admin Panel
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage users and system settings
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                User Management
                            </CardTitle>
                            <CardDescription>
                                {users.length} total users
                            </CardDescription>
                        </div>
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search users..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-users-input"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center h-[300px]">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>User</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Joined</TableHead>
                                        <TableHead className="w-[50px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredUsers.map(userItem => (
                                        <TableRow key={userItem.id} data-testid={`user-row-${userItem.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-9 w-9">
                                                        <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                                                            {getInitials(userItem.name)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-medium">{userItem.name}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {userItem.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {userItem.role === 'admin' ? (
                                                    <Badge className="bg-fuchsia-500/20 text-fuchsia-500 border-fuchsia-500/30">
                                                        <ShieldCheck className="mr-1 h-3 w-3" />
                                                        Admin
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">
                                                        Member
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {userItem.is_active ? (
                                                    <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                                                        Active
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-destructive/20 text-destructive border-destructive/30">
                                                        Inactive
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {format(new Date(userItem.created_at), 'MMM d, yyyy')}
                                            </TableCell>
                                            <TableCell>
                                                {userItem.id !== user?.id && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon">
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            {userItem.role === 'member' ? (
                                                                <DropdownMenuItem
                                                                    onClick={() => openActionDialog(userItem, 'promote')}
                                                                >
                                                                    <ShieldCheck className="mr-2 h-4 w-4" />
                                                                    Make Admin
                                                                </DropdownMenuItem>
                                                            ) : (
                                                                <DropdownMenuItem
                                                                    onClick={() => openActionDialog(userItem, 'demote')}
                                                                >
                                                                    <ShieldOff className="mr-2 h-4 w-4" />
                                                                    Remove Admin
                                                                </DropdownMenuItem>
                                                            )}
                                                            {userItem.is_active ? (
                                                                <DropdownMenuItem
                                                                    className="text-destructive focus:text-destructive"
                                                                    onClick={() => openActionDialog(userItem, 'deactivate')}
                                                                >
                                                                    <UserX className="mr-2 h-4 w-4" />
                                                                    Deactivate
                                                                </DropdownMenuItem>
                                                            ) : (
                                                                <DropdownMenuItem
                                                                    onClick={() => openActionDialog(userItem, 'activate')}
                                                                >
                                                                    <UserCheck className="mr-2 h-4 w-4" />
                                                                    Activate
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="capitalize">{actionType} User</DialogTitle>
                    </DialogHeader>
                    <p>{getActionMessage()}</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant={actionType === 'deactivate' ? 'destructive' : 'default'}
                            onClick={handleAction}
                        >
                            Confirm
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
